import React from 'react';
import { GitCompare, ShieldAlert } from 'lucide-react';
import { Callout } from '../../components/ui/index.js';
import { humanize } from './admin-format.js';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function formatPercent(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value * 100)}%` : 'Unknown';
}

function scoreValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return value.toFixed(2);
  if (typeof value === 'string' && value.trim()) return value;
  return 'Unknown';
}

interface TileProps {
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
}

const Tile: React.FC<TileProps> = ({ label, value, sub }) => (
  <div className="rounded-lg border bg-surface-alt p-3">
    <p className="text-xs text-muted">{label}</p>
    <p className="mt-1 text-lg font-semibold text-heading">{value}</p>
    <p className="text-xs text-muted">{sub}</p>
  </div>
);

interface ActionRequestEvidenceProps {
  evidence: unknown;
}

/** The evidence attached to an action request: a structured view for template upgrades, JSON for anything else. */
export const ActionRequestEvidence: React.FC<ActionRequestEvidenceProps> = ({ evidence }) => {
  const root = asRecord(evidence);
  const upgrade = asRecord(root?.templateUpgrade);

  if (!root) return <p className="text-sm text-muted">No evidence was recorded for this request.</p>;
  if (!upgrade) {
    return (
      <pre className="max-h-72 overflow-auto rounded-lg bg-surface-alt p-3 text-xs text-heading">
        {JSON.stringify(root, null, 2)}
      </pre>
    );
  }

  const tests = asRecord(upgrade.tests) ?? {};
  const scores = asRecord(upgrade.scores) ?? {};
  const baselineScores = asRecord(scores.baseline) ?? {};
  const candidateScores = asRecord(scores.candidate) ?? {};
  const consumers = asArray(upgrade.affectedConsumers)
    .map(asRecord)
    .filter((c): c is Record<string, unknown> => c !== null);
  const compatibility = asRecord(upgrade.compatibilityDiff) ?? {};
  const decisionContext = asRecord(upgrade.decisionContext) ?? {};
  const comparison = asRecord(decisionContext.comparison) ?? {};
  const scoreKeys = Array.from(new Set([...Object.keys(baselineScores), ...Object.keys(candidateScores)])).slice(0, 8);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-surface-alt p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-heading">
          <GitCompare size={16} aria-hidden="true" />
          Template upgrade evidence
        </div>
        <p className="mt-2 text-sm text-muted">{String(upgrade.summary ?? 'No summary recorded.')}</p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Tile
          label="Tests"
          value={`${String(tests.passed ?? 0)}/${String(tests.total ?? 0)}`}
          sub={humanize(String(tests.status ?? 'not run'))}
        />
        <Tile
          label="Compatibility"
          value={humanize(String(compatibility.status ?? 'unknown'))}
          sub={`Risk: ${String(compatibility.riskLevel ?? 'unknown')}`}
        />
        <Tile
          label="Comparison"
          value={humanize(String(comparison.decision ?? 'inconclusive'))}
          sub={`Confidence ${formatPercent(comparison.confidence)}`}
        />
        <Tile label="Affected" value={consumers.length} sub="Consumers" />
      </div>

      {scoreKeys.length > 0 && (
        <div className="rounded-lg border bg-surface-alt">
          <div className="grid grid-cols-[1fr_90px_90px] gap-2 border-b px-3 py-2 text-xs font-medium text-muted">
            <span>Score</span>
            <span>Baseline</span>
            <span>Candidate</span>
          </div>
          {scoreKeys.map((key) => (
            <div key={key} className="grid grid-cols-[1fr_90px_90px] gap-2 px-3 py-2 text-sm">
              <span className="text-heading">{humanize(key)}</span>
              <span className="text-muted">{scoreValue(baselineScores[key])}</span>
              <span className="text-muted">{scoreValue(candidateScores[key])}</span>
            </div>
          ))}
        </div>
      )}

      {consumers.length > 0 && (
        <div className="rounded-lg border bg-surface-alt">
          <div className="border-b px-3 py-2 text-xs font-medium text-muted">Affected consumers</div>
          <ul className="divide-y">
            {consumers.slice(0, 12).map((consumer, index) => (
              <li key={`${String(consumer.kind)}-${String(consumer.id)}-${index}`} className="px-3 py-2 text-sm">
                <p className="font-medium text-heading">
                  {String(consumer.parentName ?? consumer.name ?? consumer.id)}
                </p>
                <p className="text-xs text-muted">
                  {humanize(String(consumer.kind ?? 'consumer'))} ·{' '}
                  {humanize(String(consumer.pinnedVersionStatus ?? 'unpinned'))}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {decisionContext.highRisk === true && (
        <Callout tone="danger" icon={ShieldAlert} title="High-risk upgrade">
          {String(decisionContext.recommendation ?? 'This upgrade needs review before it is adopted.')}
        </Callout>
      )}
    </div>
  );
};
